import React, { useState } from 'react'
import { useVideo } from '../../context/VideoContext'
import { Play, Clock, Calendar, Tag } from 'lucide-react'

export const VideoCard = ({ video, layout = 'grid' }) => {
  const { selectVideo } = useVideo()
  const [imageError, setImageError] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  const handlePlay = (e) => {
    e.stopPropagation()
    e.preventDefault()
    console.log('VideoCard: Clicking video:', video.title, 'ID:', video.id)
    selectVideo(video)
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const thumbnailUrl = imageError
    ? '/placeholder-video.jpg'
    : (video.thumbnail_url || '/placeholder-video.jpg')

  if (layout === 'list') {
    return (
      <div
        onClick={handlePlay}
        onMouseDown={handlePlay}
        onTouchStart={handlePlay}
        className="flex bg-gray-900 rounded-lg overflow-hidden hover:bg-gray-800 transition-colors cursor-pointer group"
        style={{ pointerEvents: 'auto', position: 'relative', zIndex: 1 }}
      >
        {/* Thumbnail */}
        <div className="relative w-48 h-28 flex-shrink-0">
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-800 animate-pulse flex items-center justify-center">
              <div className="text-gray-600">📹</div>
            </div>
          )}
          <img
            src={thumbnailUrl}
            alt={video.title}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            className={`w-full h-full object-cover transition-opacity ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
          
          {/* Play overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all">
            <div className="bg-netflix-red rounded-full p-2 opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all">
              <Play className="w-4 h-4 text-white fill-current" />
            </div>
          </div>
          
          {/* Duration badge */}
          {video.duration && (
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
              <Clock className="w-3 h-3 inline mr-1" />
              {formatDuration(video.duration)}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <h3 className="text-white font-semibold text-lg mb-2 line-clamp-1">
            {video.title}
          </h3>
          
          <p className="text-gray-400 text-sm mb-3 line-clamp-2">
            {video.description}
          </p>
          
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Tag className="w-3 h-3" />
                <span className="capitalize">{video.category}</span>
              </div>
              
              <div className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>{formatDate(video.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Grid layout
  return (
    <div
      onClick={handlePlay}
      onMouseDown={handlePlay}
      onTouchStart={handlePlay}
      className="video-card group cursor-pointer block w-full h-full"
      style={{
        pointerEvents: 'auto',
        position: 'relative',
        zIndex: 10,
        minHeight: '200px',
        backgroundColor: 'rgba(255,0,0,0.1)' // Temporary red overlay to see clickable area
      }}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video mb-3">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gray-800 animate-pulse rounded-lg flex items-center justify-center">
            <div className="text-gray-600 text-2xl">📹</div>
          </div>
        )}
        <img
          src={thumbnailUrl}
          alt={video.title}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          className={`w-full h-full object-cover rounded-lg transition-opacity ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        />
        
        {/* Play overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 rounded-lg flex items-center justify-center transition-all">
          <div className="bg-netflix-red rounded-full p-3 opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all">
            <Play className="w-6 h-6 text-white fill-current" />
          </div>
        </div>
        
        {/* Duration badge */}
        {video.duration && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
            {formatDuration(video.duration)}
          </div>
        )}
      </div>

      {/* Video Info */}
      <div>
        <h3 className="text-white font-semibold mb-1 line-clamp-2 text-sm">
          {video.title}
        </h3>
        
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span className="capitalize">{video.category}</span>
          <span>{formatDate(video.createdAt)}</span>
        </div>
        
        {video.description && (
          <p className="text-gray-500 text-xs mt-1 line-clamp-2">
            {video.description}
          </p>
        )}
      </div>
    </div>
  )
}